import { HttpParams } from '@angular/common/http'
import { Component, Input, OnDestroy, OnInit, WritableSignal } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { WjGridModule } from '@grapecity/wijmo.angular2.grid'
import { WjGridFilterModule } from '@grapecity/wijmo.angular2.grid.filter'
import { WjInputModule } from '@grapecity/wijmo.angular2.input'
import { DataMap } from '@grapecity/wijmo.grid'

import { AutoSizeDirective, BaseComponent, FeatureType, Grid, ListItem, ModalSize, QualityControlRoad, VpJob } from '../../../shared'
import { ActionToolbarButtonsComponent } from '../../../shared/reusable-components/action-toolbar-buttons/action-toolbar-buttons.component'
import { ActionToolbarCount } from '../../../shared/reusable-components/action-toolbar-count/action-toolbar-count.component'
import { ActionToolbarSearchComponent } from '../../../shared/reusable-components/action-toolbar-search/action-toolbar-search.component'
import { RoadModalComponent } from './road-modal.component'

@Component({
    selector: 'app-quality-control-road',
    templateUrl: './road.component.html',
    standalone: true,
    imports: [
        FormsModule,
        WjGridModule,
        WjGridFilterModule,
        WjInputModule,
        ActionToolbarCount,
        ActionToolbarButtonsComponent,
        ActionToolbarSearchComponent,
        AutoSizeDirective,
    ],
})
export class RoadComponent extends BaseComponent implements OnInit, OnDestroy {
    @Input() selectedJob!: WritableSignal<string>
    @Input() allJobs: VpJob[] = []

    grid: Grid<QualityControlRoad>
    aggregateVendors: any[] = []
    allVendors: any[] = []
    supplierMap!: DataMap
    testingAgencyMap!: DataMap
    subContractorMap!: DataMap

    constructor() {
        super()
        this.grid = new Grid<QualityControlRoad>(this.services)
            .setupModalEditing(
                this.services.data.qualityControlRoads,
                'Road Quality Control',
                RoadModalComponent,
                () => new QualityControlRoad(),
                this,
                ModalSize.large,
            )
            .filterBy(item => [item.streetAreaName])
            .withColumnFilters()
            .isReadOnly(true)
            .wrapColumnHeaders()

        this.grid.itemAdded.on(this.handleItemAdded.bind(this))
        this.grid.modified.on(this.refreshData.bind(this))
    }

    get canEdit() {
        return this.services.authentication.hasFeature([FeatureType.FieldToolsPlansAndSpecsEdit])
    }

    get selectedJobName(): string {
        const jobId = this.selectedJob()
        if (!jobId) return ''
        const job = this.allJobs.find(j => j.id === jobId)
        return job ? `${job.id} ${job.name}` : jobId
    }

    ngOnInit() {
        this.loadVendors()
        this.loadAllVendors()
    }

    ngOnDestroy(): void {
        this.grid.itemAdded.off(this.handleItemAdded.bind(this))
        this.grid.modified.off(this.refreshData.bind(this))
        this.grid.destroy()
    }

    loadVendors() {
        this.services.data.get<any[]>('api/Vendor/GetForAggregate').subscribe({
            next: (result: any[]) => {
                this.aggregateVendors = result
                this.buildSupplierMap()
            },
            error: () => {
                this.services.notification.error('Failed to load vendors')
            },
        })
    }

    loadAllVendors() {
        this.services.data.get<any[]>('api/Vendor').subscribe({
            next: (result: any[]) => {
                this.allVendors = result || []
                this.buildTestingAgencyMap()
                this.buildSubContractorMap()
            },
            error: () => {
                this.services.notification.error('Failed to load vendors')
            },
        })
    }

    buildSupplierMap() {
        const supplierOptions: ListItem<number | null>[] = []
        supplierOptions.push(new ListItem<number | null>(null, ''))
        this.aggregateVendors.forEach(x => {
            supplierOptions.push(new ListItem<number | null>(x.id, x.name))
        })
        this.supplierMap = new DataMap(supplierOptions, 'value', 'display')
    }

    buildTestingAgencyMap() {
        const options: ListItem<number | null>[] = []
        options.push(new ListItem<number | null>(null, ''))
        this.allVendors.forEach(x => {
            options.push(new ListItem<number | null>(x.id, x.name))
        })
        this.testingAgencyMap = new DataMap(options, 'value', 'display')
    }

    buildSubContractorMap() {
        const options: ListItem<number | null>[] = []
        options.push(new ListItem<number | null>(null, ''))
        this.allVendors.forEach(x => {
            options.push(new ListItem<number | null>(x.id, x.name))
        })
        this.subContractorMap = new DataMap(options, 'value', 'display')
    }

    loadRoadData(jobId: string) {
        if (!jobId || jobId === '') {
            this.grid.items = []
            return
        }

        const params = new HttpParams().set('jobId', jobId)
        this.services.data.get<QualityControlRoad[]>('api/QualityControlRoad/GetByJob', params).subscribe({
            next: (result: QualityControlRoad[]) => {
                this.grid.items = result || []
            },
            error: () => {
                this.services.notification.error('Failed to load road data')
                this.grid.items = []
            },
        })
    }

    handleItemAdded(item: QualityControlRoad) {
        item.job = this.selectedJob()
    }

    refreshData() {
        const jobId = this.selectedJob()
        if (jobId && jobId !== '') {
            this.loadRoadData(jobId)
        }
    }

    onGridInitialized() {
        // Grid initialized
    }
}
